import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ScanLine, ShoppingBag, Users, TrendingDown, Loader2,
  Receipt, UserCheck, Crown, Calendar,
} from 'lucide-react';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: '7 days' },
  { key: 'month', label: '30 days' },
  { key: 'year', label: '1 year' },
];

function formatPHP(n) {
  return `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

/**
 * MerchantTransactionsPage — analytics + recent ledger of every QR scan.
 *
 * KPIs at top, top-5 customers in the middle, scrollable recent-transactions
 * list at the bottom. Range selector switches all three views together.
 */
export function MerchantTransactionsPage() {
  const { api } = useAuth();
  const [range, setRange] = useState('week');
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, r] = await Promise.all([
        api.get(`/api/merchant/scan/stats?range=${range}`),
        api.get('/api/merchant/scan/recent?limit=50'),
      ]);
      setStats(s.data);
      setRecent(r.data.transactions || []);
    } catch (e) {
      console.error('Failed to load transactions:', e);
    }
    setLoading(false);
  }, [api, range]);

  useEffect(() => { load(); }, [load]);

  return (
    <MemberSidebarLayout>
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-outfit text-2xl font-semibold text-white">Transactions & Analytics</h1>
            <p className="text-[#A0B4CC] text-sm mt-1">Every QR scan, check-in, and sale you've processed.</p>
          </div>
          <Link to="/merchant/scan">
            <Button className="bg-[#00E5FF] text-[#000B18] hover:bg-[#00E5FF]/90" data-testid="goto-scan-btn">
              <ScanLine className="w-4 h-4 mr-2" />
              Scan member
            </Button>
          </Link>
        </div>

        {/* Range selector */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="range-selector">
          {RANGES.map(r => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                range === r.key
                  ? 'bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/40'
                  : 'bg-[#041A3B] text-[#A0B4CC] border-white/10 hover:text-white'
              }`}
              data-testid={`range-${r.key}-btn`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {loading && !stats ? (
          <div className="py-20 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
          </div>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                label="Total scans"
                value={stats?.total_scans || 0}
                icon={ScanLine}
                accent="#00E5FF"
                testId="kpi-total-scans"
              />
              <KpiCard
                label="Sales revenue"
                value={formatPHP(stats?.total_revenue || 0)}
                icon={ShoppingBag}
                accent="#00E676"
                hint={`${stats?.sales_count || 0} sale${stats?.sales_count === 1 ? '' : 's'}`}
                testId="kpi-revenue"
              />
              <KpiCard
                label="Discount given"
                value={formatPHP(stats?.total_discount_given || 0)}
                icon={TrendingDown}
                accent="#FFD740"
                hint="Member savings"
                testId="kpi-discount"
              />
              <KpiCard
                label="Unique members"
                value={stats?.unique_members || 0}
                icon={Users}
                accent="#7C4DFF"
                hint={`${stats?.checkin_count || 0} check-in${stats?.checkin_count === 1 ? '' : 's'}`}
                testId="kpi-members"
              />
            </div>

            {/* Top members */}
            {stats?.top_members?.length > 0 && (
              <Card className="bg-[#041A3B] border-white/10">
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Crown className="w-4 h-4 text-[#FFD740]" />
                    <h2 className="font-outfit text-lg text-white">Most-frequent customers</h2>
                  </div>
                  <div className="space-y-2" data-testid="top-members-list">
                    {stats.top_members.map((m, i) => (
                      <div key={m.member_id} className="flex items-center gap-3 p-3 rounded-lg bg-[#000B18] border border-white/5">
                        <div className="w-8 h-8 rounded-full bg-[#041A3B] border border-white/10 flex items-center justify-center text-[#00E5FF] text-xs font-semibold shrink-0">
                          {i + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{m.member_name}</p>
                          <p className="text-[#5C7599] text-xs">{m.visits} visit{m.visits === 1 ? '' : 's'}</p>
                        </div>
                        <span className="text-[#00E676] text-sm font-mono shrink-0">{formatPHP(m.total_spent)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recent transactions */}
            <Card className="bg-[#041A3B] border-white/10">
              <CardContent className="p-5">
                <h2 className="font-outfit text-lg text-white mb-4">Recent transactions</h2>
                {recent.length === 0 ? (
                  <div className="py-12 text-center">
                    <Receipt className="w-10 h-10 text-[#5C7599] mx-auto mb-3 opacity-50" />
                    <p className="text-[#A0B4CC] text-sm">No transactions yet. Run your first scan to get started.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5" data-testid="recent-transactions-list">
                    {recent.map(t => (
                      <TransactionRow key={t.id} t={t} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </MemberSidebarLayout>
  );
}

function KpiCard({ label, value, icon: Icon, accent, hint, testId }) {
  return (
    <Card className="bg-[#041A3B] border-white/10" data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-[#A0B4CC] text-xs uppercase tracking-wider">{label}</p>
            <p className="font-outfit text-2xl text-white mt-1 truncate" style={{ color: accent }}>{value}</p>
            {hint && <p className="text-[#5C7599] text-[11px] mt-1">{hint}</p>}
          </div>
          <Icon className="w-5 h-5 shrink-0 opacity-60" style={{ color: accent }} />
        </div>
      </CardContent>
    </Card>
  );
}

function TransactionRow({ t }) {
  const isCheckin = t.type === 'checkin';
  return (
    <div className="py-3 flex items-center gap-3" data-testid={`txn-row-${t.id}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${isCheckin ? 'bg-[#00E5FF]/10 text-[#00E5FF]' : 'bg-[#00E676]/10 text-[#00E676]'}`}>
        {isCheckin ? <UserCheck className="w-4 h-4" /> : <Receipt className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{t.member_name}</p>
        <p className="text-[#5C7599] text-[11px] mt-0.5 truncate">
          {t.service_name || (isCheckin ? 'Check-in' : 'Custom sale')}
          {' · '}
          {formatDate(t.created_at)}
        </p>
      </div>
      <div className="text-right shrink-0">
        {!isCheckin ? (
          <>
            <p className="text-white font-mono text-sm">{formatPHP(t.amount_charged)}</p>
            {t.discount_amount > 0 && (
              <p className="text-[#00E676] text-[11px]">−{formatPHP(t.discount_amount)} ({t.discount_pct}%)</p>
            )}
          </>
        ) : (
          <Badge className="bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30 text-[10px]">Check-in</Badge>
        )}
      </div>
    </div>
  );
}

export default MerchantTransactionsPage;
